import { queryOptions, useQuery } from '@tanstack/react-query';

import {
  Cluster,
  NodeTemplateConstraintStateEnum,
  NodeTemplateEdgeLocationConfig,
  SelectedInstanceType,
} from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

import { useAutoscalerFeaturesQuery } from './useAutoscalerFeaturesQuery';

import { isAKSCluster } from '@cast/shared-cluster-context-data-access';

const SELECTION_TEMPLATE_PREVIEW_QUERY_KEY =
  'autoscaler-selection-template-preview';

type FilterOptions = {
  burstable?: NodeTemplateConstraintStateEnum;
  customerSpecific?: NodeTemplateConstraintStateEnum;
  fractional?: NodeTemplateConstraintStateEnum;
  azs?: string[];
  spot?: boolean | null;
  onDemand?: boolean | null;
  useSpotFallbacks?: boolean | null;
  edgeLocationConfigs?: NodeTemplateEdgeLocationConfig[];
  configurationId?: string;
  os?: string[];
};

const getSelectionTemplatePreviewQueryKey = (
  clusterId: string,
  options: FilterOptions
) => [SELECTION_TEMPLATE_PREVIEW_QUERY_KEY, clusterId, options] as const;

type UseSelectedInstanceTypesQuery = {
  isLoading: boolean;
  data?: SelectedInstanceType[];
  error: unknown;
};

const getSelectionTemplatePreviewQueryOptions = ({
  cluster,
  options,
  features,
  isSuccess,
}: {
  cluster: Cluster;
  options: FilterOptions;
  features: ReturnType<typeof useAutoscalerFeaturesQuery>['features'];
  isSuccess: boolean;
}) =>
  queryOptions({
    queryKey: getSelectionTemplatePreviewQueryKey(cluster.id, options),
    queryFn: async () => {
      const { data } = await apiClient.autoscaler.getInstanceTypes({
        clusterId: cluster.id,
        filter: {
          configurationId: options.configurationId,
          edgeLocationConfigs: options.edgeLocationConfigs,
          constraints: {
            architectures: features?.arm64Supported
              ? ['amd64', 'arm64']
              : ['amd64'],
            os: options.os?.length
              ? options.os
              : isAKSCluster(cluster) && features?.windowsAutoscalingEnabled
              ? ['linux', 'windows']
              : ['linux'],
            burstable: options.burstable,
            customerSpecific: options.customerSpecific,
            azs: options.azs,
            spot: options.spot,
            onDemand: options.onDemand,
            useSpotFallbacks: options.useSpotFallbacks,
            gpu: { fractionalGpus: options.fractional },
          },
        },
      });

      return data.availableInstanceTypes || [];
    },
    enabled: !!cluster.id && isSuccess,
  });

export const useSelectedInstanceTypesQuery = (
  cluster: Cluster,
  options: FilterOptions = {}
): UseSelectedInstanceTypesQuery => {
  const { features, isSuccess } = useAutoscalerFeaturesQuery(cluster.id);
  const { isLoading, data, error } = useQuery(
    getSelectionTemplatePreviewQueryOptions({
      cluster,
      options,
      features,
      isSuccess,
    })
  );

  return {
    isLoading,
    data,
    error,
  };
};
